import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingOverlay } from '@/components/elements/LoadingOverlay';
import { Forbidden } from '@/components/pages/Forbidden';
import { ROUTE_PATH } from '@/constants/routes';
import { ROLES } from '@/constants/users';
import { type UserRole } from '@/contexts/AuthContext';
import { useAuth } from '@/hooks/useAuth';
import { buildSuiteLoungeEntryPath } from '@/hooks/useSuiteLoungeFlow';
import { useUser } from '@/hooks/useUser';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: readonly UserRole[];
}

export const ProtectedRoute = ({
  children,
  allowedRoles,
}: ProtectedRouteProps) => {
  const { isAuthenticated, isLoaded, role } = useAuth();
  const { wasSuiteLoungeMember } = useUser();
  const navigate = useNavigate();

  // ロール制限がある場合、許可されているロールかどうかを判定
  const hasRequiredRole = !allowedRoles || !role || allowedRoles.includes(role);

  useEffect(() => {
    if (!isLoaded) return;
    // ログインしていない場合はログイン画面にリダイレクト
    if (!isAuthenticated) {
      // ロールに応じてリダイレクト先を分岐
      // - スタッフ専用ページ (allowedRolesが['staff']のみ): /staff (スタッフログインページ)
      // - それ以外 (一般ユーザー、共有端末、複数ロール許可ページ): / (ログインページ)
      const isStaffOnly =
        allowedRoles?.length === 1 && allowedRoles[0] === ROLES.STAFF;
      // allowedRoles 未指定＝個人端末の一般ユーザーページ。
      // SL会員はトークン期限切れ後もSL導線(?entry=sl)を維持する。
      // 共有端末/スタッフページ(allowedRoles指定あり)は対象外。
      const isGeneralUserPage = !allowedRoles;

      const loginPath = isStaffOnly
        ? ROUTE_PATH.STAFF_LOGIN
        : isGeneralUserPage && wasSuiteLoungeMember
          ? buildSuiteLoungeEntryPath(ROUTE_PATH.LOGIN)
          : ROUTE_PATH.LOGIN;

      navigate(loginPath, { replace: true });
      return;
    }
  }, [isAuthenticated, navigate, isLoaded, allowedRoles, wasSuiteLoungeMember]);

  // 認証状態の復元中(期限切れトークンのrefresh往復を含む)は、まだ遷移先を決められない。
  // ここで null を返すと往復のあいだ白画面になるため、ローディングを出して待つ。
  if (!isLoaded) return <LoadingOverlay />;

  // ロール制限に違反している場合はエラー表示
  if (isAuthenticated && !hasRequiredRole) {
    return <Forbidden />;
  }

  // useEffectでリダイレクト処理をしていても一瞬childrenが描画されてしまうので、防ぐための早期return
  if (!isAuthenticated) return null;

  return <>{children}</>;
};
